/**
 * Global keybinding handler for region-panel actions (Item 9c) and zoom (D2).
 *
 * Bindings:
 *   Cmd/Ctrl+G        - Create stack (requires region selection + no active input).
 *   Shift+Cmd/Ctrl+G  - Disband stack (requires a selected region that is in a stack).
 *   + / = / Shift+=   - Zoom in by 1.1x around viewport centre.
 *   - / _  / Shift+-  - Zoom out by 1/1.1x around viewport centre.
 *
 * The listener is attached to `document` in the capture phase so it fires before
 * any component's keydown handler.
 */

import { t } from '../i18n';
import type { Store } from '../state';
import { createStack, disbandStack, nextUnusedStackN } from '../util/stack-ops';
import { pushUndoSnapshot } from '../util/region-undo';
import {
  getPrimaryShortcutLetter,
  getShortcutKeys,
} from './keyboard-shortcuts';
import type { ZoomStore } from './zoom-state';

const CREATE_STACK_KEY = getPrimaryShortcutLetter('shortcuts.createStack');
const DISBAND_STACK_KEY = getPrimaryShortcutLetter('shortcuts.disbandStack');
const [ZOOM_IN_KEY = '+', ZOOM_OUT_KEY = '-'] =
  getShortcutKeys('shortcuts.zoom').split('/');

/** Returns true when focus is currently inside a text input or textarea. */
function focusInInput(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

/**
 * Install global keybinding listeners. Returns a teardown function that removes
 * the listeners.
 *
 * @param store - The application state store (required).
 * @param zoomStore - Optional zoom store. When provided, +/- keys adjust zoom.
 */
export function installKeybindings(store: Store, zoomStore?: ZoomStore): () => void {
  const handleKeydown = (event: KeyboardEvent): void => {
    // Never fire when the user is typing in an input or textarea.
    if (focusInInput()) return;

    // D2 - +/= zooms in, -/_ zooms out.
    // On US keyboards '+' is Shift+=, so the resulting event.key is '+' with
    // shiftKey=true. We match on the resolved key character, not on modifiers,
    // so both the plain and shifted forms work. We skip Cmd/Ctrl-modified
    // variants to avoid overriding browser shortcuts (e.g. Ctrl+=).
    if (!event.metaKey && !event.ctrlKey && !event.altKey) {
      if (event.key === ZOOM_IN_KEY || event.key === '=') {
        event.preventDefault();
        if (zoomStore) {
          // Zoom toward the approximate viewport centre.
          const vw = window.innerWidth / 2;
          const vh = window.innerHeight / 2;
          zoomStore.zoomToward({ x: vw, y: vh }, 1.1);
        }
        return;
      }
      if (event.key === ZOOM_OUT_KEY || event.key === '_') {
        event.preventDefault();
        if (zoomStore) {
          const vw = window.innerWidth / 2;
          const vh = window.innerHeight / 2;
          zoomStore.zoomToward({ x: vw, y: vh }, 1 / 1.1);
        }
        return;
      }
    }

    // Only fire the remaining bindings when Cmd (Mac) or Ctrl (Windows/Linux) is held.
    const isModifier = event.metaKey || event.ctrlKey;
    if (!isModifier) return;

    const key = event.key.toLowerCase();

    const isCreateStack = !event.shiftKey && key === CREATE_STACK_KEY;
    const isDisbandStack = event.shiftKey && key === DISBAND_STACK_KEY;

    if (isCreateStack || isDisbandStack) {
      event.preventDefault();

      const state = store.get();
      const selected = state.activeRegionUuids;

      if (isDisbandStack) {
        // Shift+Cmd+G - Disband stack.
        // Requires at least one selected region that belongs to a stack.
        const anyInStack = selected.some((uuid) => {
          const entry = state.regionEntries.find((e) => e.uuid === uuid);
          return entry?.stackId !== null;
        });
        if (!anyInStack) return;

        // Collect all stacks containing selected regions.
        const stackUuidsToDisband = new Set<string>();
        for (const entry of state.regionEntries) {
          if (entry.stackId !== null && selected.includes(entry.uuid)) {
            stackUuidsToDisband.add(entry.stackId);
          }
        }
        if (stackUuidsToDisband.size === 0) return;

        const undoStack = pushUndoSnapshot(state);
        let patchedState = state;
        const combinedPatch: Partial<typeof state> = {};

        for (const uuid of stackUuidsToDisband) {
          const p = disbandStack(patchedState, uuid);
          if (p.regionStacks !== undefined) combinedPatch.regionStacks = p.regionStacks;
          if (p.regionEntries !== undefined) combinedPatch.regionEntries = p.regionEntries;
          patchedState = { ...patchedState, ...p };
        }
        store.patch({ ...combinedPatch, regionUndoStack: undoStack });

      } else {
        // Cmd+G - Create stack.
        // Requires at least one selected region.
        if (selected.length < 1) return;

        const undoStack = pushUndoSnapshot(state);
        const PLACEHOLDER = '__N__';
        const stackNTemplate = t('label.stackN', { n: PLACEHOLDER });
        const prefix = stackNTemplate.replace(PLACEHOLDER, '').trim();
        const n = nextUnusedStackN(state.regionStacks, prefix);
        const userName = t('label.stackN', { n });
        const patch = createStack(state, { memberUuids: selected, userName });
        store.patch({ ...patch, regionUndoStack: undoStack });
      }
    }
  };

  document.addEventListener('keydown', handleKeydown, true);

  return () => {
    document.removeEventListener('keydown', handleKeydown, true);
  };
}
